import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-hot-toast";
import { toastApiError } from "@/lib/errorMessages";
import { MoreVertical } from "lucide-react";
import { Button, Card, Modal } from "@/components/ui";
import { BackLink, ErrorState, LoadingState } from "@/components/common";
import {
  TicketDetailHeaderCard,
  TicketSidebar,
  CATEGORY_CONFIG,
  ticketCategoryLabel,
  ticketStatusLabel,
  formatFullDate,
  TicketStatus,
  TicketPriority,
} from "@/components/tickets";
import {
  supportTicketKeys,
  supportTicketService,
} from "@/services/supportTicketService";
import {
  StorageUploadError,
  getStorageUploadErrorMessage,
} from "@/lib/storageUpload";
import { useStorageUploadBatch } from "@/hooks/useStorageUploadBatch";
import { StorageUploadProgressBanner } from "@/components/common/StorageUploadProgressBanner";
import {
  SupportTicketChat,
  type SupportTicketAttachment,
} from "@/components/chat";

export default function AdminTicketDetailPage() {
  const { t: tCommon } = useTranslation("common");
  const { t } = useTranslation("admin");
  const { t: ts } = useTranslation("support");
  const { t: tComponents } = useTranslation("components");
  const { ticketId } = useParams<{ ticketId: string }>();
  const queryClient = useQueryClient();
  const { activeUpload, uploadTicketAttachments } = useStorageUploadBatch();
  const [showSidebar, setShowSidebar] = useState(false);

  const {
    data: ticket,
    isLoading,
    isFetching,
    isError,
    refetch,
  } = useQuery({
    queryKey: supportTicketKeys.adminDetail(ticketId ?? ""),
    queryFn: () => supportTicketService.getTicket(ticketId!),
    enabled: Boolean(ticketId),
  });

  const invalidateTicket = async () => {
    await queryClient.invalidateQueries({
      queryKey: supportTicketKeys.adminDetail(ticketId!),
    });
    await queryClient.invalidateQueries({
      queryKey: supportTicketKeys.lists(),
    });
    await queryClient.invalidateQueries({
      queryKey: supportTicketKeys.adminStats(),
    });
  };

  const sendMutation = useMutation({
    mutationFn: async ({
      message,
      attachments,
      isInternalNote,
    }: {
      message: string;
      attachments: SupportTicketAttachment[];
      isInternalNote: boolean;
    }) => {
      const files = attachments.map((attachment) => attachment.file);
      const uploadedAttachments = await uploadTicketAttachments(
        ticketId!,
        files,
      );
      await supportTicketService.addMessage(ticketId!, {
        content: message,
        visibility: isInternalNote ? "ADMIN_ONLY" : "PARTICIPANTS",
        attachment_urls: uploadedAttachments,
      });
      return isInternalNote;
    },
    onSuccess: async (isInternalNote) => {
      toast.success(isInternalNote ? t("internalNoteAdded") : t("replySent"));
      await invalidateTicket();
    },
    onError: (error) => {
      if (error instanceof StorageUploadError) {
        toast.error(getStorageUploadErrorMessage(error, tComponents));
      } else {
        toastApiError(error, "generic", t("sendMessageError"));
      }
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: { status?: TicketStatus; priority?: TicketPriority }) =>
      supportTicketService.updateTicket(ticketId!, data),
    onSuccess: async (_result, variables) => {
      if (variables.status != null) {
        toast.success(
          t("ticketMarked", { label: ticketStatusLabel(variables.status) }),
        );
      } else {
        toast.success(t("priorityUpdated"));
      }
      await invalidateTicket();
    },
    onError: (error, variables) => {
      toastApiError(
        error,
        "generic",
        variables.status != null ? t("updateStatusError") : t("updatePriorityError"),
      );
    },
  });

  if (isLoading) {
    return (
      <LoadingState
        className="flex-1"
        message={ts("adminDetail.loadingTicket")}
      />
    );
  }

  if (isError || !ticket) {
    return (
      <ErrorState
        className="flex-1"
        message={ts("adminDetail.notFound")}
        action={
          <BackLink
            variant="outline"
            fallback="/admin/tickets"
          >
            {ts("adminDetail.backToTickets")}
          </BackLink>
        }
      />
    );
  }

  const categoryInfo = CATEGORY_CONFIG[ticket.category];
  const sidebarTicket = {
    status: ticket.status,
    priority: ticket.priority,
    category: ticket.category,
    owner_user_id: ticket.owner_user_id,
    owner_name: ticket.owner_name,
    owner_email: ticket.owner_email,
    owner_role: ticket.owner_role,
    created_at: ticket.created_at,
    updated_at: ticket.updated_at,
    resolved_at: ticket.resolved_at,
    message_count: ticket.messages.length,
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4 lg:flex-row lg:items-stretch">
      {/* Main Content */}
      <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-4">
        <TicketDetailHeaderCard
          backFallback="/admin/tickets"
          backAriaLabel={ts("adminDetail.backToTickets")}
          ticketNumber={ticket.ticket_number}
          subject={ticket.subject}
          status={ticket.status}
          priority={ticket.priority}
          categoryEmoji={categoryInfo.emoji}
          categoryLabel={ticketCategoryLabel(ticket.category)}
          openedOnLabel={ts("adminDetail.openedOn", {
            date: formatFullDate(ticket.created_at),
          })}
          action={
            <Button
              variant="outline"
              size="icon"
              onClick={() => setShowSidebar(true)}
              className="lg:hidden"
              aria-label={ts("adminDetail.ticketDetails")}
            >
              <MoreVertical className="h-5 w-5" />
            </Button>
          }
        />

        <Card className="flex min-h-0 flex-1 flex-col overflow-hidden p-0">
          {activeUpload ? (
            <div className="border-b border-surface-100 p-3">
              <StorageUploadProgressBanner upload={activeUpload} />
            </div>
          ) : null}
          <SupportTicketChat
            messages={ticket.messages.map((message) => ({
              id: message.id,
              content: message.content,
              is_from_admin: message.is_from_admin,
              visibility: message.visibility,
              created_at: message.created_at,
              sender_name: message.sender_name,
              attachments: message.attachments.map((attachment) => ({
                id: attachment.id,
                file_name: attachment.file_name,
                file_url: attachment.file_url,
                file_type: attachment.file_type as
                  | "image"
                  | "video"
                  | "document",
                file_size: attachment.file_size,
              })),
            }))}
            perspective="admin"
            status={ticket.status}
            isSending={sendMutation.isPending}
            onSend={async (message, attachments, isInternalNote) => {
              await sendMutation.mutateAsync({
                message,
                attachments,
                isInternalNote,
              });
            }}
            showInternalNoteToggle
            title={ticket.owner_name || ts("messageBubble.sellerFallback")}
            avatarInitials={ticket.owner_name
              ?.split(/\s+/)
              .map((part) => part[0])
              .join("")
              .slice(0, 2)
              .toUpperCase()}
            subtitle={ts("adminDetail.messagesCount", {
              count: ticket.messages.length,
            })}
            sendPlaceholder={ts("adminDetail.replyPlaceholder")}
            onRefresh={() => void refetch()}
            isRefreshing={isFetching && !isLoading}
            closedFooter={
              ticket.status === TicketStatus.CLOSED ? (
                <p className="text-sm text-surface-500">
                  {ts("adminDetail.ticketClosed")}{" "}
                  <button
                    type="button"
                    onClick={() =>
                      updateMutation.mutate({ status: TicketStatus.OPEN })
                    }
                    className="font-medium text-primary-600 hover:text-primary-700"
                  >
                    {ts("adminDetail.reopenTicket")}
                  </button>
                </p>
              ) : undefined
            }
          />
        </Card>
      </div>

      {/* Sidebar - Desktop */}
      <Card className="hidden w-80 shrink-0 self-start lg:block">
        <TicketSidebar
          ticket={sidebarTicket}
          onStatusChange={(status) => updateMutation.mutate({ status })}
          onPriorityChange={(priority) => updateMutation.mutate({ priority })}
          isUpdating={updateMutation.isPending}
          showOwnerProfileLink
        />
      </Card>

      <Modal
        closeLabel={tCommon("close")}
        isOpen={showSidebar}
        onClose={() => setShowSidebar(false)}
        title={ts("adminDetail.ticketDetails")}
        size="lg"
        priority="high"
      >
        <TicketSidebar
          ticket={sidebarTicket}
          onStatusChange={(status) => updateMutation.mutate({ status })}
          onPriorityChange={(priority) => updateMutation.mutate({ priority })}
          isUpdating={updateMutation.isPending}
          showOwnerProfileLink
        />
      </Modal>
    </div>
  );
}
